import { strToU8, zipSync } from 'fflate';
import type { ReportDataWarning, ReportGeneration } from './generated-report';

export type TerritorialTrend = 'down' | 'up' | 'same';

export type TerritorialMetricKey =
  | 'incomplete-activation'
  | 'uncabled-street-number'
  | 'missed-appointment-delivery'
  | 'missed-appointment-assurance'
  | 'appointment-request-change'
  | 'maintenance-assurance'
  | 'damage'
  | 'poor-work'
  | 'network-connection-missing'
  | 'road-surface-restoration'
  | 'never-contacted';

export type TerritorialMetric = {
  key: TerritorialMetricKey;
  label: string;
};

export type TerritorialAnalysisRow = {
  key: string;
  label: string;
  values: number[];
  previousValues: number[];
  trends: TerritorialTrend[];
};

export type TerritorialMarketKey = 'residential' | 'business-pa';

export type TerritorialMarketTable = {
  key: TerritorialMarketKey;
  label: string;
  customerTypeLabel: string;
  sourceRows: number;
  previousSourceRows?: number;
  unresolvedRows: number;
  previousUnresolvedRows?: number;
  rows: TerritorialAnalysisRow[];
  totals: {
    values: number[];
    previousValues: number[];
    trends: TerritorialTrend[];
  };
};

export type TerritorialSourceFile = {
  id: string;
  name: string;
  path: string;
  modifiedTime: string | null;
  webViewLink: string | null;
};

export type TerritorialAnalysis = {
  selectedYear: number;
  selectedMonth: number;
  periodLabel: string;
  previousYear: number;
  previousMonth: number;
  previousPeriodLabel: string;
  topicLabel: string;
  metrics: TerritorialMetric[];
  markets: TerritorialMarketTable[];
  masterFiles: TerritorialSourceFile[];
  worksheetName: string;
  dataWarnings?: ReportDataWarning[];
  generation?: ReportGeneration;
};

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function columnName(index: number): string {
  let current = index + 1;
  let result = '';
  while (current > 0) {
    const remainder = (current - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    current = Math.floor((current - 1) / 26);
  }
  return result;
}

function inlineCell(reference: string, value: string, style: number): string {
  return `<c r="${reference}" t="inlineStr" s="${style}"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

function numberCell(reference: string, value: number, style: number): string {
  return `<c r="${reference}" s="${style}"><v>${Number.isFinite(value) ? value : 0}</v></c>`;
}

function valueStyle(trend: TerritorialTrend, alternate: boolean): number {
  if (trend === 'down') return alternate ? 7 : 6;
  if (trend === 'up') return alternate ? 9 : 8;
  return alternate ? 5 : 4;
}

function marketSheet(analysis: TerritorialAnalysis, market: TerritorialMarketTable): string {
  const lastColumn = columnName(analysis.metrics.length);
  const dataRows = market.rows.map((row, index) => {
    const excelRow = index + 3;
    const alternate = index % 2 === 1;
    const cells = [
      inlineCell(`A${excelRow}`, row.label, alternate ? 3 : 2),
      ...row.values.map((value, metricIndex) => numberCell(
        `${columnName(metricIndex + 1)}${excelRow}`,
        value,
        valueStyle(row.trends[metricIndex] ?? 'same', alternate),
      )),
    ].join('');
    return `<row r="${excelRow}" ht="25" customHeight="1">${cells}</row>`;
  }).join('');
  const totalRow = market.rows.length + 3;
  const totalCells = [
    inlineCell(`A${totalRow}`, 'Totale', 10),
    ...market.totals.values.map((value, index) => numberCell(`${columnName(index + 1)}${totalRow}`, value, 10)),
  ].join('');
  const groupTitle = `Social Subreason (Social Reason analizzata: Problemi e Reclami e Solleciti) ${market.key === 'residential' ? 'Mercato Residenziale' : 'Mercato Business e PA'}`;

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="A1:${lastColumn}${totalRow}"/>
  <sheetViews><sheetView workbookViewId="0"><pane xSplit="1" ySplit="2" topLeftCell="B3" activePane="bottomRight" state="frozen"/></sheetView></sheetViews>
  <sheetFormatPr defaultRowHeight="20"/>
  <cols><col min="1" max="1" width="38" customWidth="1"/><col min="2" max="${analysis.metrics.length + 1}" width="20" customWidth="1"/></cols>
  <sheetData>
    <row r="1" ht="26" customHeight="1">${inlineCell('A1', 'Code', 1)}${inlineCell('B1', groupTitle, 1)}</row>
    <row r="2" ht="56" customHeight="1">${inlineCell('A2', '', 1)}${analysis.metrics.map((metric, index) => inlineCell(`${columnName(index + 1)}2`, metric.label, 1)).join('')}</row>
    ${dataRows}
    <row r="${totalRow}" ht="27" customHeight="1">${totalCells}</row>
  </sheetData>
  <mergeCells count="1"><mergeCell ref="B1:${lastColumn}1"/></mergeCells>
  <pageMargins left="0.25" right="0.25" top="0.35" bottom="0.35" header="0.15" footer="0.15"/>
  <pageSetup orientation="landscape" fitToWidth="1" fitToHeight="1"/>
</worksheet>`;
}

function workbookFiles(analysis: TerritorialAnalysis): Record<string, Uint8Array> {
  const residential = analysis.markets.find(({ key }) => key === 'residential');
  const business = analysis.markets.find(({ key }) => key === 'business-pa');
  if (!residential || !business) throw new Error('Tabelle territoriali incomplete.');
  const now = new Date().toISOString();
  return {
    '[Content_Types].xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/worksheets/sheet2.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`),
    '_rels/.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`),
    'docProps/core.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:title>Analisi territoriale ${escapeXml(analysis.periodLabel)}</dc:title><dc:creator>Talos</dc:creator>
  <dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created><dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified>
</cp:coreProperties>`),
    'docProps/app.xml': strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"><Application>Talos</Application></Properties>'),
    'xl/workbook.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <bookViews><workbookView xWindow="0" yWindow="0" windowWidth="24000" windowHeight="12000"/></bookViews>
  <sheets><sheet name="Mercato residenziale" sheetId="1" r:id="rId1"/><sheet name="Business e PA" sheetId="2" r:id="rId2"/></sheets>
  <calcPr calcId="191029" fullCalcOnLoad="1"/>
</workbook>`),
    'xl/_rels/workbook.xml.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet2.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
    'xl/styles.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="5">
    <font><sz val="11"/><color rgb="FF111111"/><name val="Arial"/></font>
    <font><b/><sz val="11"/><color rgb="FF111111"/><name val="Arial"/></font>
    <font><b/><sz val="11"/><color rgb="FF1FA354"/><name val="Arial"/></font>
    <font><b/><sz val="11"/><color rgb="FFFF1919"/><name val="Arial"/></font>
    <font><b/><sz val="11"/><color rgb="FF111111"/><name val="Arial"/></font>
  </fonts>
  <fills count="5"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFF04A91"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFD9D9D9"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFFFFFFF"/></patternFill></fill></fills>
  <borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border><border><left style="thin"><color rgb="FF111111"/></left><right style="thin"><color rgb="FF111111"/></right><top style="thin"><color rgb="FF111111"/></top><bottom style="thin"><color rgb="FF111111"/></bottom><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="11">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="1" fillId="4" borderId="1" xfId="0" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="1" fillId="3" borderId="1" xfId="0" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>
    <xf numFmtId="3" fontId="4" fillId="4" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="3" fontId="4" fillId="3" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="3" fontId="2" fillId="4" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="3" fontId="2" fillId="3" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="3" fontId="3" fillId="4" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="3" fontId="3" fillId="3" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="3" fontId="1" fillId="3" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
  </cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`),
    'xl/worksheets/sheet1.xml': strToU8(marketSheet(analysis, residential)),
    'xl/worksheets/sheet2.xml': strToU8(marketSheet(analysis, business)),
  };
}

export function createTerritorialAnalysisWorkbook(analysis: TerritorialAnalysis): Uint8Array {
  return zipSync(workbookFiles(analysis), { level: 6 });
}

export function territorialAnalysisWorkbookFileName(year: number, month: number): string {
  return `analisi-territoriale-${year}-${String(month).padStart(2, '0')}.xlsx`;
}
